<script setup>
import { ref, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import StickyActionBar from '@/Components/UI/StickyActionBar.vue'
import { useCurrency } from '@/composables/useCurrency.js'
import { NATIONALITIES } from '@/composables/useNationalities.js'

defineOptions({ layout: AppLayout })

const props = defineProps({
  rental:    { type: Object, required: true },
  customer:  { type: Object, required: true },
  vehicle:   { type: Object, required: true },
  office:    { type: Object, default: () => ({}) },
  prefill:   { type: Object, default: () => ({}) },
  contracts: { type: Array,  default: () => [] },
  default_signature_url: { type: String, default: null },
})

const { formatDateTime } = useCurrency()

const isEdit = computed(() => props.contracts.length > 0)

const formatDate = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
}
const formatTime = (iso) => {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

const p = props.prefill
const address       = ref(p.address       ?? props.customer.address ?? '')
const nationality   = ref(p.nationality   ?? props.customer.nationality ?? '')
const passportNo    = ref(p.passport_no   ?? props.customer.passport_no ?? '')
const handoverWhere = ref(p.handover_where ?? props.rental.handover_address ?? props.office.address ?? '')
const returnWhere   = ref(p.return_where  ?? props.rental.return_address ?? props.office.address ?? '')
const helmets       = ref(Number(p.helmets ?? 1))
const fuelLevel     = ref(p.fuel_level    ?? 'Full')
const passportCopy  = ref(p.passport_copy ?? 'Yes')
const totalAmount   = ref(p.total_amount  ?? props.rental.final_total ?? props.rental.estimated_total ?? '')
const deposit       = ref(p.deposit       ?? props.rental.deposit ?? '')

const returnDate = computed(() => formatDate(props.rental.expected_end_at))
const startDate  = computed(() => formatDate(props.rental.start_at))
const startTime  = computed(() => props.rental.handover_time || formatTime(props.rental.start_at))
const returnTime = computed(() => props.rental.return_time || formatTime(props.rental.expected_end_at))

const saving = ref(false)

function goBack() {
  if (typeof window !== 'undefined' && window.history.length > 1) {
    window.history.back()
  } else {
    router.visit(`/rentals/${props.rental.id}`)
  }
}

function savePdf() {
  saving.value = true
  router.post(`/rentals/${props.rental.id}/contract`, {
    nationality:    nationality.value,
    passport_no:    passportNo.value,
    address:        address.value,
    handover_where: handoverWhere.value,
    return_where:   returnWhere.value,
    total_amount:   totalAmount.value,
    deposit:        deposit.value,
    passport_copy:  passportCopy.value,
    helmets:        helmets.value,
    fuel_level:     fuelLevel.value,
  }, {
    preserveScroll: true,
    replace: true,
    onFinish: () => { saving.value = false },
  })
}
</script>

<template>
  <!-- eslint-disable vue/no-restricted-class -->
  <div class="p-6 max-w-3xl mx-auto">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
      <button
        type="button"
        class="text-gray-400 hover:text-gray-600"
        aria-label="Back"
        @click="goBack"
      >
        <svg
          class="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      <h2 class="text-xl font-bold text-gray-900">
        {{ isEdit ? 'Edit Contract' : 'New Contract' }} — #{{ rental.id }}
      </h2>
    </div>

    <!-- Saved versions -->
    <div
      v-if="contracts.length"
      class="bg-white rounded-xl border border-gray-200 p-4 mb-4 space-y-2"
    >
      <h2 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Saved versions
      </h2>
      <div
        v-for="c in contracts"
        :key="c.version"
        class="flex items-center justify-between text-sm"
      >
        <span class="text-gray-700">Version {{ c.version }} — {{ formatDateTime(c.created_at) }}</span>
        <a
          v-if="c.pdf_url"
          :href="c.pdf_url"
          target="_blank"
          class="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 inline-flex items-center gap-1"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          /></svg>
          Download
        </a>
      </div>
    </div>

    <!-- Contract body -->
    <div class="bg-white rounded-xl border border-gray-200 p-8 space-y-5">
      <h2 class="text-center text-lg font-bold text-gray-900 uppercase tracking-wide">
        Scooter Rental Agreement
      </h2>

      <div class="space-y-3 text-sm text-gray-800">
        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Date:</span>
          <span class="flex-1 border-b border-gray-400 min-h-[1.25rem] pb-0.5">{{ startDate }}</span>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Scooter registered:</span>
          <span class="flex-1 border-b border-gray-400 min-h-[1.25rem] pb-0.5">{{ vehicle.registration }}</span>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Model:</span>
          <span class="flex-1 border-b border-gray-400 min-h-[1.25rem] pb-0.5">{{ vehicle.make }} {{ vehicle.model }}</span>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Name of renter:</span>
          <span class="flex-1 border-b border-gray-400 min-h-[1.25rem] pb-0.5">{{ customer.full_name }}</span>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Nationality:</span>
          <select
            v-model="nationality"
            class="flex-1 min-w-0 border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5"
          >
            <option value="">…</option>
            <option
              v-for="n in NATIONALITIES"
              :key="n"
              :value="n"
            >
              {{ n }}
            </option>
          </select>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Passport number:</span>
          <input
            v-model="passportNo"
            type="text"
            placeholder="…"
            class="flex-1 border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5"
          >
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Address in Thailand:</span>
          <input
            v-model="address"
            type="text"
            placeholder="…"
            class="flex-1 border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5"
          >
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-medium">Telephone:</span>
          <span class="flex-1 border-b border-gray-400 min-h-[1.25rem] pb-0.5">{{ customer.phone || '' }}</span>
        </div>

        <hr class="border-gray-300 my-2">

        <div class="flex items-baseline gap-6 flex-wrap">
          <div class="flex items-baseline gap-2">
            <span class="font-bold">From:</span>
            <span class="border-b border-gray-400 min-w-[100px] pb-0.5">{{ startDate }}</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="font-bold">Time:</span>
            <span class="border-b border-gray-400 min-w-[80px] pb-0.5">{{ startTime }}</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="font-bold">Duration (days):</span>
            <span class="border-b border-gray-400 min-w-[60px] pb-0.5">{{ rental.duration_days ?? '' }}</span>
          </div>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-bold">Handover place:</span>
          <input
            v-model="handoverWhere"
            type="text"
            placeholder="…"
            class="flex-1 border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5"
          >
        </div>

        <div class="flex items-baseline gap-6 flex-wrap">
          <div class="flex items-baseline gap-2">
            <span class="font-bold">Scooter/Motorbike will be returned on:</span>
            <span class="border-b border-gray-400 min-w-[100px] pb-0.5">{{ returnDate }}</span>
          </div>
          <div class="flex items-baseline gap-2">
            <span class="font-bold">Time:</span>
            <span class="border-b border-gray-400 min-w-[80px] pb-0.5">{{ returnTime }}</span>
          </div>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="w-52 shrink-0 font-bold">Where:</span>
          <input
            v-model="returnWhere"
            type="text"
            placeholder="…"
            class="flex-1 border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5"
          >
        </div>

        <div class="flex items-baseline gap-6">
          <div class="flex items-baseline gap-2 flex-1">
            <span class="font-bold shrink-0">Total amount:</span>
            <input
              v-model="totalAmount"
              type="text"
              class="flex-1 border-b border-gray-400 bg-transparent text-sm font-medium outline-none pb-0.5"
            >
          </div>
          <div class="flex items-baseline gap-2 w-56">
            <span class="font-bold shrink-0">Deposit:</span>
            <input
              v-model="deposit"
              type="text"
              class="flex-1 border-b border-gray-400 bg-transparent text-sm font-medium outline-none pb-0.5"
            >
          </div>
        </div>

        <div class="flex items-baseline gap-2">
          <span class="font-medium flex-1">I agree to leave a copy of my passport/documents as a guarantee:</span>
          <input
            v-model="passportCopy"
            type="text"
            placeholder="Yes / No"
            class="w-24 border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5 text-center"
          >
        </div>

        <div
          v-if="vehicle.vehicle_class !== 'four_wheeler'"
          class="flex items-baseline gap-2"
        >
          <span class="font-medium">I received</span>
          <select
            v-model="helmets"
            class="border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5 text-center"
          >
            <option
              v-for="n in [0,1,2,3,4,5]"
              :key="n"
              :value="n"
            >
              {{ n }}
            </option>
          </select>
          <span class="font-medium">helmet(s).</span>
        </div>

        <hr class="border-gray-300 my-2">

        <div class="flex items-baseline gap-2">
          <span class="font-bold">Fuel level:</span>
          <select
            v-model="fuelLevel"
            class="border-b border-gray-400 bg-transparent text-sm outline-none pb-0.5"
          >
            <option>Full</option>
            <option>¾</option>
            <option>½</option>
            <option>¼</option>
            <option>Empty</option>
          </select>
        </div>

        <div class="flex justify-between mt-10 pt-4 gap-8">
          <div class="flex-1 text-center">
            <div class="h-12" />
            <div class="border-t border-gray-400 pt-2 text-xs text-gray-500">
              Renter signature
            </div>
          </div>
          <div class="flex-1 text-center">
            <div class="h-12 flex items-end justify-center">
              <img
                v-if="default_signature_url"
                :src="default_signature_url"
                alt="Owner / Agent signature"
                class="max-h-12 object-contain"
              >
            </div>
            <div class="border-t border-gray-400 pt-2 text-xs text-gray-500">
              Owner / Agent signature
            </div>
          </div>
        </div>
      </div>
    </div>

    <StickyActionBar>
      <UiButton
        type="button"
        variant="primary"
        :loading="saving"
        @click="savePdf"
      >
        {{ saving ? 'Saving…' : 'Save PDF' }}
      </UiButton>
    </StickyActionBar>
  </div>
</template>
